import { oc } from 'ts-optchain';
import { MetadataApi } from 'vrlcm-gui-api';
import { EnvironmentRequestProduct, GroupMetadata } from 'vrlcm-gui-types';
import { Http } from 'vrlcm-gui-utils';
import {
  GET_NODES_CARDINALITY_URL,
  GET_NODE_ADVANCED_PROPERTIES_METADATA_URL,
  GET_NODE_DEFAULT_PROPERTIES_METADATA_URL,
  GET_PRODUCT_CLUSTER_VIP_METADATA_URL,
  GET_PRODUCT_METADATA_URL
} from '../../../constants/ApiUri';
import { advancedDisplayMetadata } from '../../../mocks/AdvancedPropertiesDisplay';
import {
  nodeDisplayMetadata,
  productDisplayMetadata
} from '../../../mocks/CreateEnvironment';
import { DisplayGroupMetadata } from '../../../types/Common';
import { GET_AVAILABLE_TENANTS_URL } from '../../../constants/ApiUri';
import {
  ComponentList,
  ProductMetadata
} from '../../../types/workflows/steps/Product';
import { UIEnvironmentState } from 'components/lcops/types/Environments';

export default class ProductStepAPI {
  public static getProductPropertiesMetadata = async (
    productId: string,
    version: string,
    selectedProducts: string[],
    isImport: boolean,
    vcfEnabledEnvironment?: boolean
  ): Promise<GroupMetadata[]> => {
    if (isImport) {
      return MetadataApi.getImportProductPropertiesMetadata(
        productId,
        vcfEnabledEnvironment
      );
    } else {
      return MetadataApi.getProductPropertiesMetadata(
        productId,
        version,
        selectedProducts,
        vcfEnabledEnvironment || false
      );
    }
  };

  public static getProductPropertiesDisplayMetadata = async (
    productId: string,
    selectedProducts: string[],
    isImport: boolean,
    isInstallFlow: boolean,
    isVcfEnvironment: boolean,
    isAddProduct: boolean,
    isVidmInstalled: boolean,
    existingProducts: EnvironmentRequestProduct[],
    environmentsWithVrops: UIEnvironmentState[],
    selectedVsscTenantId?: string,
    vsscVersionSupportingIntegration?: boolean,
    authenticationType?: string
  ): Promise<DisplayGroupMetadata> => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (
          productDisplayMetadata(
            selectedProducts,
            isImport,
            isInstallFlow,
            isVcfEnvironment,
            isAddProduct,
            isVidmInstalled,
            existingProducts,
            environmentsWithVrops,
            selectedVsscTenantId,
            vsscVersionSupportingIntegration,
            authenticationType
          )[productId]
        ) {
          resolve({
            name: productId,
            properties: productDisplayMetadata(
              selectedProducts,
              isImport,
              isInstallFlow,
              isVcfEnvironment,
              isAddProduct,
              isVidmInstalled,
              existingProducts,
              environmentsWithVrops,
              selectedVsscTenantId,
              vsscVersionSupportingIntegration,
              authenticationType
            )[productId]
          });
        } else {
          resolve({ name: productId, properties: [] });
        }
      });
    });
  };

  public static getNodeDisplayMetadata = async (
    productId: string,
    nodeType: string
  ): Promise<DisplayGroupMetadata> => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (nodeDisplayMetadata[nodeType]) {
          resolve({
            name: nodeType,
            properties: nodeDisplayMetadata[nodeType]
          });
        } else if (
          nodeType.startsWith('iaas') ||
          nodeType === 'db' ||
          nodeType === 'proxy-agent-vsphere'
        ) {
          resolve({
            name: nodeType,
            properties: nodeDisplayMetadata['iaas-node']
          });
        } else {
          resolve({
            name: nodeType,
            properties: nodeDisplayMetadata['generic-node']
          });
        }
      });
    });
  };

  public static getNodeDefaultPropertiesMetadata = async (
    product: string,
    nodeType: string
  ): Promise<GroupMetadata> => {
    const cachedMetadata =
      ProductStepAPI.nodeMetadataCache[`${product}/${nodeType}`];
    if (cachedMetadata) {
      return Promise.resolve(cachedMetadata[0]);
    }
    const response = await Http.get(
      GET_NODE_DEFAULT_PROPERTIES_METADATA_URL(product, nodeType)
    );
    ProductStepAPI.nodeMetadataCache[`${product}/${nodeType}`] = response.data;
    return response.data[0];
  };

  public static getNodeAdvancedPropertiesMetadata = async (
    product: string,
    nodeType: string
  ): Promise<GroupMetadata[]> => {
    const cachedMetadata =
      ProductStepAPI.advancedPropertyMetadataCache[`${product}/${nodeType}`];
    if (cachedMetadata) {
      return Promise.resolve(cachedMetadata);
    }

    const response = await Http.get(
      GET_NODE_ADVANCED_PROPERTIES_METADATA_URL(product, nodeType)
    );

    ProductStepAPI.advancedPropertyMetadataCache[`${product}/${nodeType}`] =
      response.data;
    return response.data;
  };

  public static getNodeAdvancedPropertiesDisplayMetadata = async (
    product: string,
    nodeType: string,
    group: string
  ): Promise<DisplayGroupMetadata> => {
    const displayMetadata = {
      name: group,
      properties: oc(advancedDisplayMetadata)[product][nodeType][group]([])
    };
    return Promise.resolve(displayMetadata);
  };

  public static getProductsMetadata = async (
    productId: string,
    version: string,
    size: string,
    isImport: boolean,
    selectedProducts: string[],
    nodeCount?: number,
    haEnable?: boolean,
    vcfEnabledEnvironment?: boolean,
    authenticationType?: string
  ): Promise<ProductMetadata> => {
    const data = {
      deploymentType: size,
      productName: productId,
      version,
      selectedProducts,
      vcfEnabledEnvironment: vcfEnabledEnvironment || false,
      authenticationType: authenticationType
    };

    if (haEnable) {
      Object.assign(data, { haEnable });
    }

    if (nodeCount) {
      Object.assign(data, { nodeCount });
    }
    if (!isImport) {
      const [product, clusterVip] = await Promise.all([
        Http.post(GET_PRODUCT_METADATA_URL, data),
        Http.get(GET_PRODUCT_CLUSTER_VIP_METADATA_URL(productId, version))
      ]);
      return {
        ...product.data,
        clusterVip: clusterVip.data
      };
    } else {
      const productProperties = await MetadataApi.getImportProductPropertiesMetadata(
        productId,
        vcfEnabledEnvironment
      );
      return {
        productName: productId,
        productVersion: version,
        deploymentType: size,
        componentList: {},
        productProperties,
        clusterVip: [],
        productNodes: []
      };
    }
  };

  public static getClusterVipMetadata = async (
    product: string,
    version: string
  ): Promise<GroupMetadata[]> => {
    const response = await Http.get(
      GET_PRODUCT_CLUSTER_VIP_METADATA_URL(product, version)
    );
    return response.data;
  };

  public static getNodesCardinality = async (
    product: string,
    version: string
  ): Promise<ComponentList> => {
    const response = await Http.get(
      GET_NODES_CARDINALITY_URL(product, version)
    );
    return response.data;
  };

  private static advancedPropertyMetadataCache: {
    [key: string]: GroupMetadata[];
  } = {};

  private static nodeMetadataCache: {
    [key: string]: GroupMetadata[];
  } = {};

  public static getAvailableTenants = async (
    environmentId: string,
    productId: string
  ) => {
    const response = await Http.post<string[]>(
      GET_AVAILABLE_TENANTS_URL(environmentId, productId)
    );
    return response.data;
  };
}
